import {
  PublicClientApplication,
  type Configuration,
  type IPublicClientApplication
} from '@azure/msal-browser';
import { User } from 'firebase/auth';

import {
  getSsoProviderByFirebaseId,
  isEntraSsoFirebaseProvider,
  resolveEffectiveSignInProvider
} from '@/configuration/ssoProviders';
import {
  EntraAccessRevokeReason,
  getEntraAccessRevokeReason
} from '@/helpers/LoginHelper';
import { SsoProviderConfig } from '@/types/ssoProvider';

/** Firebase refreshes ID tokens when fewer than 5 minutes remain. */
const FIREBASE_TOKEN_REFRESH_WINDOW_MS = 5 * 60 * 1000;

const MSAL_SILENT_SCOPES = ['openid', 'profile', 'email'];

export type EntraRevalidationOutcome = 'ok' | 'not_assigned' | 'user_removed' | 'skipped';

type AccessRevokedListener = (reason: EntraAccessRevokeReason) => void;

const accessRevokedListeners = new Set<AccessRevokedListener>();

/** Subscribe to Entra access revoke (app assignment or tenant user removed/disabled). */
export const onEntraAccessRevoked = (listener: AccessRevokedListener): (() => void) => {
  accessRevokedListeners.add(listener);
  return () => {
    accessRevokedListeners.delete(listener);
  };
};

const notifyEntraAccessRevoked = (reason: EntraAccessRevokeReason): void => {
  accessRevokedListeners.forEach((listener) => listener(reason));
};

let inFlightRevalidation: Promise<EntraRevalidationOutcome> | null = null;
let lastRevalidatedTokenExp: string | null = null;

const msalInstances = new Map<string, Promise<IPublicClientApplication>>();

const getMsalInstance = (config: SsoProviderConfig): Promise<IPublicClientApplication> | null => {
  const clientId = config.clientId?.trim();
  const tenant = config.tenant?.trim() || config.customParameters?.tenant?.trim();

  if (!clientId || !tenant) {
    return null;
  }

  const cacheKey = `${clientId}:${tenant}`;
  const existing = msalInstances.get(cacheKey);
  if (existing) {
    return existing;
  }

  const msalConfig: Configuration = {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenant}`,
      // Must be registered as an SPA redirect URI on the Entra app (same origin as the webapp).
      redirectUri: window.location.origin,
      knownAuthorities: ['login.microsoftonline.com']
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false
    }
  };

  const instancePromise = (async () => {
    const pca = new PublicClientApplication(msalConfig);
    await pca.initialize();
    return pca;
  })();

  msalInstances.set(cacheKey, instancePromise);
  return instancePromise;
};

const isAccessRevokedOutcome = (
  outcome: EntraRevalidationOutcome
): outcome is EntraAccessRevokeReason =>
  outcome === 'not_assigned' || outcome === 'user_removed';

/**
 * Silent Entra check via MSAL ssoSilent (hidden iframe, no popup).
 * Succeeds if still allowed and an Entra SSO cookie exists;
 * returns not_assigned / user_removed on known revoke errors; otherwise skipped.
 */
export const revalidateEntraAppAssignment = async (
  user: User,
  signInProvider: string | null | undefined
): Promise<EntraRevalidationOutcome> => {
  if (!isEntraSsoFirebaseProvider(signInProvider)) {
    return 'skipped';
  }

  const config = getSsoProviderByFirebaseId(signInProvider);
  if (!config) {
    return 'skipped';
  }

  const msalPromise = getMsalInstance(config);
  if (!msalPromise) {
    // clientId/tenant missing — cannot check silently; do not fall back to popup.
    return 'skipped';
  }

  try {
    const msal = await msalPromise;
    const loginHint = user.email?.trim();

    await msal.ssoSilent({
      scopes: MSAL_SILENT_SCOPES,
      ...(loginHint ? { loginHint } : {})
    });

    return 'ok';
  } catch (error) {
    const reason = getEntraAccessRevokeReason(error);
    if (reason) {
      return reason;
    }
    return 'skipped';
  }
};

const msUntilTokenExpiry = async (
  user: User
): Promise<{ remainingMs: number; expirationTime: string }> => {
  const tokenResult = await user.getIdTokenResult();
  return {
    expirationTime: tokenResult.expirationTime,
    remainingMs: new Date(tokenResult.expirationTime).getTime() - Date.now()
  };
};

const runEntraRevalidation = async (
  user: User,
  signInProvider: string | null | undefined
): Promise<EntraRevalidationOutcome> => {
  if (inFlightRevalidation !== null) {
    return inFlightRevalidation;
  }

  inFlightRevalidation = (async () => {
    const outcome = await revalidateEntraAppAssignment(user, signInProvider);
    if (isAccessRevokedOutcome(outcome)) {
      notifyEntraAccessRevoked(outcome);
      return outcome;
    }
    if (outcome === 'ok') {
      const refreshed = await user.getIdTokenResult(true);
      lastRevalidatedTokenExp = refreshed.expirationTime;
    }
    return outcome;
  })().finally(() => {
    inFlightRevalidation = null;
  });

  return inFlightRevalidation;
};

/**
 * When the Firebase ID token is in the refresh window, re-check Entra access
 * before allowing a silent Firebase refresh to extend the session.
 */
export const ensureEntraAssignmentBeforeTokenRefresh = async (
  user: User
): Promise<EntraRevalidationOutcome> => {
  const tokenResult = await user.getIdTokenResult();
  const signInProvider = resolveEffectiveSignInProvider(tokenResult.signInProvider, user);

  if (!isEntraSsoFirebaseProvider(signInProvider)) {
    return 'skipped';
  }

  const { remainingMs, expirationTime } = await msUntilTokenExpiry(user);
  if (remainingMs > FIREBASE_TOKEN_REFRESH_WINDOW_MS) {
    return 'skipped';
  }

  if (lastRevalidatedTokenExp === expirationTime) {
    return 'ok';
  }

  return runEntraRevalidation(user, signInProvider);
};

/**
 * Forced check used by the idle-session timer at the Firebase refresh boundary.
 * Notifies listeners on assignment removed or tenant user removed/disabled.
 */
export const revalidateEntraAssignmentAtTokenBoundary = async (
  user: User,
  signInProvider: string | null | undefined
): Promise<EntraRevalidationOutcome> => runEntraRevalidation(user, signInProvider);

/** Delay until Firebase would refresh the ID token (expiry minus refresh window). */
export const getEntraRevalidationDelayMs = async (user: User): Promise<number> => {
  const { remainingMs } = await msUntilTokenExpiry(user);
  return Math.max(remainingMs - FIREBASE_TOKEN_REFRESH_WINDOW_MS, 0);
};

/**
 * Interactive Microsoft login. Returns the Entra ID token only.
 * Does not contact Firebase, so an unknown user does not get a Firebase account.
 */
export const signInWithEntraPopup = async (
  config: SsoProviderConfig
): Promise<{ idToken: string; email: string | null }> => {
  const msalPromise = getMsalInstance(config);
  if (!msalPromise) {
    throw new Error('Microsoft sign-in is not configured.');
  }

  const msal = await msalPromise;
  const scopes = Array.from(new Set([...(config.scopes ?? []), 'openid', 'profile', 'email']));

  try {
    const result = await msal.loginPopup({
      scopes,
      prompt: 'select_account'
    });

    if (!result.idToken) {
      throw new Error('Microsoft sign-in did not return an ID token.');
    }

    return {
      idToken: result.idToken,
      email: result.account?.username?.trim() || null
    };
  } catch (error) {
    const errorCode = (error as { errorCode?: string })?.errorCode;
    if (errorCode === 'user_cancelled') {
      throw Object.assign(new Error('Sign-in popup was closed.'), {
        code: 'auth/popup-closed-by-user'
      });
    }
    throw error;
  }
};

export const resetEntraRevalidationState = (): void => {
  lastRevalidatedTokenExp = null;
  inFlightRevalidation = null;
};

export const isEntraAccessRevokedOutcome = isAccessRevokedOutcome;
